import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AdminAPI, OdkAPI } from "../../api/endpoints";
import type { PermisoEncuesta } from "../../api/endpoints";
import { Card, PageHeader, Spinner, ErrorBanner, HelpBox } from "../../components/Layout";

export default function AdminPermissionsPage() {
  const qc = useQueryClient();
  const [userId, setUserId] = useState<number | null>(null);
  const [projectId, setProjectId] = useState<number | null>(null);
  const [formId, setFormId] = useState<string | null>(null);
  const [allowedVars, setAllowedVars] = useState("");
  // Descargar el diccionario es llevarse el cuestionario entero: se concede aparte.
  const [puedeDescargar, setPuedeDescargar] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [guardando, setGuardando] = useState<number | null>(null);

  const usersQ = useQuery({ queryKey: ["users"], queryFn: AdminAPI.users });
  const permsQ = useQuery({
    queryKey: ["permissions", userId],
    queryFn: () => AdminAPI.listPermissions(userId ?? undefined),
  });
  const projectsQ = useQuery({ queryKey: ["projects"], queryFn: OdkAPI.projects });
  const formsQ = useQuery({
    queryKey: ["forms", projectId],
    queryFn: () => OdkAPI.forms(projectId!),
    enabled: !!projectId,
  });

  const grant = async () => {
    if (!userId || !projectId || !formId) return;
    setErr(null);
    try {
      const vars = allowedVars.trim()
        ? allowedVars.split(",").map((s) => s.trim()).filter(Boolean)
        : null;
      await AdminAPI.upsertPermission({
        user_id: userId, project_id: projectId, form_id: formId,
        allowed_variables: vars, puede_descargar_diccionario: puedeDescargar,
      });
      setAllowedVars("");
      qc.invalidateQueries({ queryKey: ["permissions", userId] });
    } catch (e) { setErr(e instanceof Error ? e.message : "Error"); }
  };

  // Cambiar el atributo desde la propia fila. Se reenvía `allowed_variables` tal
  // como está para no borrarlo al guardar: el alta es idempotente y reescribe todo.
  const alternarDescarga = async (p: PermisoEncuesta) => {
    setGuardando(p.id); setErr(null);
    try {
      await AdminAPI.upsertPermission({
        user_id: p.user_id, project_id: p.project_id, form_id: p.form_id,
        allowed_variables: p.allowed_variables,
        puede_descargar_diccionario: !p.puede_descargar_diccionario,
      });
      qc.invalidateQueries({ queryKey: ["permissions", userId] });
    } catch (e) { setErr(e instanceof Error ? e.message : "No se pudo cambiar el permiso."); }
    finally { setGuardando(null); }
  };

  const revoke = async (id: number) => {
    await AdminAPI.deletePermission(id);
    qc.invalidateQueries({ queryKey: ["permissions", userId] });
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <PageHeader title="Permisos por encuesta" subtitle="Autoriza a cada usuario (encuesta, variables y descarga del diccionario)." />

      <HelpBox title="Guía: qué concede cada cosa">
        <p>
          <b>Encuesta.</b> Un usuario <b>viewer</b> solo ve —y solo puede analizar— las encuestas que
          aparezcan aquí. Los roles <b>admin</b> y <b>analyst</b> las ven todas y no necesitan permisos.
        </p>
        <p>
          <b>Variables permitidas.</b> Vacío = todas las de esa encuesta. Con una lista, el usuario solo
          ve esas (las columnas técnicas de fecha se mantienen para que las series funcionen).
        </p>
        <p>
          <b>Diccionario.</b> Consultarlo en pantalla va con el acceso a la encuesta; <b>descargarlo en
          Excel</b> es aparte, porque es llevarse el cuestionario entero —todas las preguntas, sus tipos y
          sus opciones— en un archivo. Se enciende y apaga en la columna «Diccionario» de la tabla. El
          valor con el que nacen los permisos nuevos se decide en <b>Configuración → Seguridad</b>.
        </p>
      </HelpBox>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <div className="font-medium text-marca mb-2">Conceder acceso</div>
          <label className="etiqueta">Usuario</label>
          <select onChange={(e) => setUserId(Number(e.target.value))} className="input mb-2">
            <option value="">—</option>
            {usersQ.data?.map((u) => <option key={u.id} value={u.id}>{u.email}</option>)}
          </select>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <select onChange={(e) => setProjectId(Number(e.target.value))} className="input">
              <option value="">Proyecto…</option>
              {projectsQ.data?.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <select onChange={(e) => setFormId(e.target.value)} disabled={!projectId} className="input disabled:opacity-50">
              <option value="">Formulario…</option>
              {formsQ.data?.map((f) => <option key={f.xmlFormId} value={f.xmlFormId}>{f.name}</option>)}
            </select>
          </div>
          <label className="etiqueta mt-2">Variables permitidas (vacío = todas; separar por coma)</label>
          <input value={allowedVars} onChange={(e) => setAllowedVars(e.target.value)} className="input mb-2" />
          <label className="flex items-start gap-2 text-sm mb-2 cursor-pointer">
            <input type="checkbox" checked={puedeDescargar}
              onChange={(e) => setPuedeDescargar(e.target.checked)} className="accent-marca mt-0.5" />
            <span>
              Puede descargar el diccionario en Excel
              <span className="block text-xs text-tinta-3">
                Es el cuestionario entero: todas las preguntas, sus tipos y sus opciones. Sin
                esto sigue pudiendo consultarlo en pantalla y analizar la encuesta.
              </span>
            </span>
          </label>
          <button onClick={grant} disabled={!userId || !projectId || !formId}
            className="btn">Conceder</button>
          {err && <div className="mt-2"><ErrorBanner message={err} /></div>}
        </Card>

        <Card>
          <div className="font-medium text-marca mb-2">
            Permisos {userId ? `de ${usersQ.data?.find((u) => u.id === userId)?.email ?? ""}` : "(todos)"}
          </div>
          {permsQ.isLoading && <Spinner />}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-tinta-3 border-b border-grid">
                <tr>
                  <th className="py-2 pr-3 text-left">Usuario</th>
                  <th className="text-left pr-3">Proy/Form</th>
                  <th className="text-left pr-3">Variables</th>
                  <th className="text-left pr-3">Diccionario</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {permsQ.data?.map((p) => (
                  <tr key={p.id} className="border-b border-grid">
                    {/* El correo, no el id: nadie sabe de memoria quién es el usuario 7. */}
                    <td className="py-2 pr-3 text-xs">
                      {usersQ.data?.find((u) => u.id === p.user_id)?.email ?? `#${p.user_id}`}
                    </td>
                    <td className="py-2 pr-3 text-xs">{p.project_id}/{p.form_id}</td>
                    <td className="py-2 pr-3 text-xs text-tinta-3">{p.allowed_variables ? p.allowed_variables.join(", ") : "todas"}</td>
                    <td className="py-2 pr-3">
                      <button
                        onClick={() => alternarDescarga(p)}
                        disabled={guardando === p.id}
                        title={p.puede_descargar_diccionario
                          ? "Puede descargarlo en Excel. Pulsa para quitárselo."
                          : "Solo puede consultarlo en pantalla. Pulsa para permitir la descarga."}
                        className={`text-xs px-2 py-1 rounded border disabled:opacity-50 ${
                          p.puede_descargar_diccionario
                            ? "border-estado-ok/40 bg-estado-ok/10 text-estado-ok hover:bg-estado-ok/20"
                            : "border-grid bg-plano text-tinta-3 hover:bg-grid"
                        }`}
                      >
                        {guardando === p.id ? "…" : p.puede_descargar_diccionario ? "✓ descarga" : "✕ sin descarga"}
                      </button>
                    </td>
                    <td className="py-2 pr-3 text-right">
                      <button onClick={() => revoke(p.id)} className="text-xs px-2 py-1 rounded border border-estado-critico/40 bg-estado-critico/10 text-estado-critico hover:bg-estado-critico/20">Quitar</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
